import { List } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Empty } from 'src/components/Empty/Empty';
import { Spinner } from 'src/components/Spinner/Spinner';
import { CssVariables } from 'src/styles/CssVariables';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';
import styled from 'styled-components';

type AsidePanelProps<
    T extends Entity,
    F extends Record<string, any> = Record<string, any>,
> = {
    store: BasicStore<T, F>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderItem: (item: T) => React.ReactNode;
    filterPanel?: React.ReactNode;
    header?: React.ReactNode;
};

export const MasterDetailsAside = observer(
    <T extends Entity, F extends Record<string, any> = Record<string, any>>({
        store,
        renderItem,
        header,
    }: AsidePanelProps<T, F>) => {
        const loadMoreData = async () => {
            store.pager?.setPage(store.pager.page + 1);
            await store.loadMore();
        };

        const hasMoreItems = store.pager
            ? store.accumulatedList.length < store.pager.total
            : false;

        useEffect(() => {
            store.pager?.reset();
        }, [store]);

        const listContent = (
            <List
                header={header}
                dataSource={
                    store.infiniteScroll ? store.accumulatedList : store.list
                }
                renderItem={renderItem}
                locale={{ emptyText: <Empty /> }}
            />
        );

        if (store.infiniteScroll) {
            return (
                <StyledScrollableDiv id="scrollableDiv">
                    <InfiniteScroll
                        dataLength={store.accumulatedList.length}
                        next={loadMoreData}
                        hasMore={hasMoreItems}
                        loader={
                            store.listLoader.isLoading && <StyledListLoader />
                        }
                        scrollThreshold={0.5}
                        scrollableTarget="scrollableDiv"
                    >
                        {listContent}
                    </InfiniteScroll>
                </StyledScrollableDiv>
            );
        }

        return (
            <StyledListWrapper>
                <StyledList
                    dataSource={store.list}
                    renderItem={renderItem}
                    locale={{
                        emptyText: <Empty />,
                    }}
                    pagination={getPagination(store)}
                    header={header}
                    loading={store.listLoader.isLoading}
                />
            </StyledListWrapper>
        );
    },
);

const getPagination = (store: BasicStore<any>) => {
    if (store.pager) {
        return {
            onChange: (page: number, size = 20) => {
                store.pager?.setPage(page - 1);
                store.pager?.setSize(size);
                store.loadList();
            },
            pageSize: store.pager.size,
            current: store.pager.page + 1,
            total: store.pager.total,
            showSizeChanger: false,
            responsive: true,
            showLessItems: true,
        };
    }

    return false;
};

const StyledListWrapper = styled.div`
    height: 100%;
    width: ${CssVariables.masterDetailsAsideWidth};
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding-right: 1rem;

    li a {
        width: 100%;
    }

    li {
        padding: 0;
    }
`;

const StyledScrollableDiv = styled.div`
    height: 100%;
    width: ${CssVariables.masterDetailsAsideWidth};
    overflow: auto;
    padding-right: 1rem;
    padding: 0 0 0 1.2rem;

    a {
        width: 100%;
        margin: 0.3rem 0;
    }

    li {
        padding: 0;
    }
`;

export const StyledList = styled(List)`
    flex: 1;
    display: flex;
    flex-direction: column;

    .ant-spin-nested-loading {
        flex: 1;
        padding: 0 0 0 1.2rem;
    }

    .ant-list-pagination {
        padding: 0.82rem 1.41rem 0.82rem 0.3rem;
        margin-top: 0;
    }

    .ant-list-item {
        padding: 0;
        display: block;
    }

    .ant-list-header {
        padding: 0.8rem 0 0.1rem 1.2rem;
        border: none;
    }
`;

const StyledListLoader = styled(Spinner)`
    margin: 0 auto;
    position: absolute;
    left: 10%;
    top: 50%;
    transform: translate(-50%, -50%);
`;
